import {
  EXTENSION,
  PATH_TO_IMAGES,
} from "@/sheets/utils/get-module-image-for-command";
import { Fragment } from "react";
import tw from "twin.macro";
import { ArrayCSSInterpolation } from "@emotion/serialize";
import { CheckIcon } from "lucide-react";

export type StepStatus = "pending" | "active" | "completed";

export type StepperStep = {
  /** Text displayed under the circle. */
  label: string;
  /** Visual state of the step. */
  status: StepStatus;
  image?: "up" | "unfolded" | "folded" | "valid";
};

type StepperProps = {
  /** Ordered list of steps to render. */
  steps: StepperStep[];
  className?: string;
};

/**
 * Pure, dynamic horizontal stepper.
 *
 * Renders an arbitrary number of steps as circles linked by an arrow-tipped
 * connector. Each circle and its following connector are styled from the
 * `status` of the step:
 *
 * - `pending`   → gray outlined circle, gray connector
 * - `active`    → primary outlined circle (empty), gray connector
 * - `completed` → solid primary circle, primary connector towards the next step
 *
 * The component does not own any state — parents control progression by
 * updating the `status` of each step.
 */
const Stepper = ({ steps, className }: StepperProps) => {
  return (
    <div tw="flex items-start w-full" className={className}>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const connectorActive =
          step.status === "completed" || step.status === "active";

        return (
          <Fragment key={`${step.label}-${index}`}>
            <StepMarker
              image={step.image}
              status={step.status}
              label={step.label}
              shouldColorBorder={!isLast}
            />

            {!isLast && <StepConnector active={connectorActive} />}
          </Fragment>
        );
      })}
    </div>
  );
};

type StepMarkerProps = {
  status: StepStatus;
  label: string;
  shouldColorBorder: boolean;
  image?: "up" | "unfolded" | "folded" | "valid";
  css?: ArrayCSSInterpolation;
};

const StepMarker = ({
  status,
  label,
  shouldColorBorder,
  image,
  css,
}: StepMarkerProps) => {
  const isCompleted = status === "completed";
  const isPending = status === "pending";
  const isActive = status === "active";

  return (
    <div tw="flex flex-col items-center shrink-0">
      <div>
        <div
          aria-hidden="true"
          css={[
            tw`flex items-center justify-center w-10 h-10 rounded-full border-2 overflow-hidden transition-colors`,
            !shouldColorBorder && tw`border-0`,
            isPending && tw`border-gray-300 bg-transparent`,
            isActive && tw`border-primary-600 bg-transparent`,
            isCompleted && tw`border-primary-600 bg-primary-600`,
            ...(css ?? []),
          ]}
        >
          {image === "valid" && (
            <CheckIcon
              css={[
                tw`w-8 h-8 text-gray-400`,
                isPending && tw`border-gray-300 bg-transparent`,
                isActive && tw`border-primary-600 bg-transparent`,
                isCompleted && tw`border-primary-600 text-white`,
              ]}
            />
          )}
          {image && image !== "valid" && (
            <img
              src={`${PATH_TO_IMAGES}${image}${EXTENSION}`}
              alt=""
              tw="w-8 h-8 object-contain"
            />
          )}
        </div>
      </div>
      <span
        css={[
          tw`mt-1.5 text-sm whitespace-nowrap transition-colors`,
          isPending && tw`text-gray-500`,
          isActive && tw`text-primary-800 font-semibold`,
          isCompleted && tw`text-primary-800`,
        ]}
      >
        {label}
      </span>
    </div>
  );
};

type StepConnectorProps = {
  active: boolean;
  css?: ArrayCSSInterpolation;
};

/**
 * Horizontal line with an arrow-head on the right, aligned with the vertical
 * center of the step circles.
 */
const StepConnector = ({ active, css }: StepConnectorProps) => (
  <div css={[tw`flex-1 h-10 flex items-center px-2`, ...(css ?? [])]}>
    <div
      css={[
        tw`flex-1 h-0.5 transition-colors`,
        tw`bg-gray-300`,
        active && tw`bg-primary-600`,
      ]}
    />
    <span
      aria-hidden="true"
      css={[
        tw`-ml-px w-0 h-0 transition-colors`,
        tw`border-y-4 border-y-transparent border-l-[6px]`,
        tw`border-l-gray-300`,
        active && tw`border-l-primary-600`,
      ]}
    />
  </div>
);

export default Stepper;
